import defaults from 'lodash/defaults'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'

type MutableParamState<T extends Record<string, string | null>> = {
  [K in keyof T]: T[K] extends string ? string : string | null
}

/**
 * An alternative for Next's {@link https://nextjs.org/docs/app/api-reference/functions/use-search-params | useSearchParams}
 * that supports clustering and mutating search parameters.
 */
const useMutableSearchParams = <T extends Record<string, string | null>>(initialParams?: T) => {
  const router = useRouter()
  const [baseURI, searchParams] = useMemo(() => {
    const [baseURI, searchString] = router.asPath.split('?')
    return [baseURI, new URLSearchParams(searchString)]
  }, [router.asPath])

  const [defaultParams, setDefaultParams] = useState(initialParams)
  useEffect(() => {
    setDefaultParams((defaultParams) =>
      isEqual(defaultParams, initialParams) ? defaultParams : initialParams,
    )
  }, [initialParams])

  const filterByDefaultParams = useCallback(
    <T = unknown>(obj: Record<string, T>) =>
      defaultParams ? pick(obj, Object.keys(defaultParams)) : obj,
    [defaultParams],
  )

  const params = useMemo(() => {
    const searchParamObj = Object.fromEntries(searchParams.entries())
    const filteredParamObj = defaults(filterByDefaultParams(searchParamObj), defaultParams)

    return new Proxy(filteredParamObj as MutableParamState<T>, {
      get(target, p, receiver) {
        return p in target ? Reflect.get(target, p, receiver) : null
      },
    })
  }, [defaultParams, filterByDefaultParams, searchParams])

  const getMergedSearchParams = useCallback(
    (stateToBeMerged: Partial<Record<keyof T, string | boolean>>) => {
      const newSearchParams = new URLSearchParams([...searchParams.entries()])
      for (const [key, value] of Object.entries(filterByDefaultParams(stateToBeMerged))) {
        if (value) {
          newSearchParams.set(key, value === true ? '' : value)
        } else {
          newSearchParams.delete(key)
        }
      }
      return newSearchParams
    },
    [filterByDefaultParams, searchParams],
  )

  const pushState = useCallback(
    (stateToBeMerged: Partial<Record<keyof T, string | boolean>>, shallow = true) => {
      const newSearchParams = getMergedSearchParams(stateToBeMerged)

      router.push(
        {
          pathname: baseURI,
          search: newSearchParams.toString(),
        },
        undefined,
        { shallow },
      )
    },
    [baseURI, getMergedSearchParams, router],
  )

  const replaceState = useCallback(
    (stateToBeMerged: Partial<Record<keyof T, string | boolean>>, shallow = true) => {
      const newSearchParams = getMergedSearchParams(stateToBeMerged)

      router.replace(
        {
          pathname: baseURI,
          search: newSearchParams.toString(),
        },
        undefined,
        { shallow },
      )
    },
    [baseURI, getMergedSearchParams, router],
  )

  return { params, pushState, replaceState }
}

export default useMutableSearchParams
